import styled from "styled-components";
import React, {
  CSSProperties,
  ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import THead from "./THead";
import TBody from "./TBody";
import {
  TTagColorVariants,
  TTagIconVariants,
  TTagNoIcon,
  TTagWithIcon,
} from "../Tag";
import Button, {
  TButtonIconVariants,
  TButtonProps,
  TWithIconVariants,
} from "../Button";
import Search from "../Inputs/Search";
import SVG from "../../ui/SVG";
import emptyStateSVG from "../../../assets/images/emptyState.svg";
import { TContent } from "../DropdownOnlyIcon";
import {
  FilterArea,
  NextContainer as ManifestationModal,
  TManifestationStatusOptions,
  TextArea,
  TextAreaContainer,
} from "../DropdownOnlyIcon/Menu";
import Dropdown from "../Dropdown";
import {
  backendErrorMessageHandler,
  getQuerystringParams,
} from "../../../utils/miscellaneous";
import {
  TInvoiceManifestastionStatus,
  setToastState,
} from "../../../redux/reducers/globalState";
import { TOrganization } from "../../../redux/reducers/organizations";
import apiService from "../../../services/api";
import { useTypedDispatch } from "../../../hooks/useTypedDispatch";
import {
  downloadInvoicesCompressedFiles,
  downloadIssuedInvoicesCompressedFiles,
  downloadPaymentSlipsCompressedFiles,
  fetchInvoices,
} from "../../../redux/actions";
import axios, { CancelTokenSource } from "axios";
import { Property } from "csstype";
import { useTypedSelector } from "../../../hooks/useTypedSelector";
import Skeleton, { TSkeletonProps } from "../Skeleton";
import { UilInfoCircle, UilTimes } from "@iconscout/react-unicons";

const Container = styled.section<{ $customMarginTop?: string }>`
  text-align: left;

  background: var(--color-light-0);

  margin-top: ${({ $customMarginTop }) => $customMarginTop || "27px"};

  & > button {
    margin-bottom: 18px;
    width: 100%;
  }

  & * tr td > div {
    position: static;

    button + menu {
      right: calc(110vw / 12.86);
    }

    div + *:not(menu, menu *, .recipient-data div) {
      margin-left: 12px;
    }
  }

  @media (max-width: 1450px) {
    & * tr td > div {
      button + menu {
        right: 50px !important;
      }
    }
  }
`;

const TableContainer = styled.table<{
  $isBatchOperationsContainerVisible?: boolean;
  $customHeight?: string;
  $isLoading?: boolean;
}>`
  display: block;

  ${({ $isLoading }) => $isLoading && "overflow-x: hidden;"}

  ${({ $customHeight }) =>
    $customHeight &&
    `height: ${$customHeight}; max-height: ${$customHeight}; overflow-y: auto;overflow-x: hidden;`}

  border-collapse: collapse;

  ${({ $isBatchOperationsContainerVisible }) =>
    $isBatchOperationsContainerVisible &&
    `thead {
      top: 75px;
    tr {
      border-radius: 0 0 8px 8px;
    }
  }`}
`;

const EmptyStateTBody = styled.tbody`
  display: block;

  tr {
    display: block;

    &:first-child div {
      margin-top: 64px;
      margin-bottom: 16px;
    }
  }

  td {
    display: block;

    width: 100%;

    div {
      justify-content: center;
      display: flex;
      width: 100%;

      h2 {
        font-family: var(--font-title-bold);
        color: var(--color-text-title);
        font-size: 21px;
      }

      p {
        font-family: var(--font-paragraph-default);
        color: var(--color-text-paragraph);
        font-size: 14px;
      }
    }
  }
`;

const TableFooter = styled.tfoot<{
  $isFixedAtTheBottom?: boolean;
  $customBottomPosition?: string;
  $customStyles?: { width: string };
}>`
  ${({ $isFixedAtTheBottom, $customBottomPosition, $customStyles }) =>
    $isFixedAtTheBottom &&
    `position: fixed;` +
      `bottom: ${$customBottomPosition || "14px"};` +
      ($customStyles
        ? `width: ${$customStyles.width};`
        : `width: calc(100vw - 48px);`)}

  &,
  tr,
  td {
    display: block;
    background: var(--color-light-0);
  }

  .footerDataContainer {
    display: flex;
    justify-content: space-between;
    align-items: center;

    height: 74px;

    padding-right: 24px;

    border: 1px solid var(--color-light-200);
    border-radius: 8px 8px 0px 0px;
    box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.1);

    & > * {
      margin-left: 12px;
    }

    .leftSideFooterDataContainer,
    .rightSideFooterDataContainer {
      display: flex;
      align-items: center;

      & > * {
        margin-left: 12px;
      }
    }

    .leftSideFooterDataContainer {
      & > div {
        width: 50px;
        div > button {
          height: 30px;
          min-height: unset;

          p.content {
            top: 7px;
            left: 3px;
            font-size: 12px;
          }

          svg {
            top: 4px;
            right: 0px;
          }
        }
        menu {
          top: -126px;

          & > button {
            min-height: unset;
            height: 36px;

            & > p.content {
              font-size: 12px;
              left: 3px;
            }

            &.active {
              & > p.content {
                color: var(--color-light-0);
                top: 10px;
              }
              &::after {
                font-size: 18px;
                top: 50%;
                right: 8px;
              }
            }
          }
        }
      }
    }

    .rightSideFooterDataContainer {
      button.active {
        background: var(--color-main-blue);
        border: 1px solid var(--color-main-blue);
        color: var(--color-light-0);
      }
    }

    p {
      font-size: 14px;
      font-family: var(--font-paragraph-default);
      color: var(--color-text-paragraph);
    }
  }

  .footerFormButtonContainer {
    margin: 24px 0;
    display: flex;
    justify-content: flex-end;

    button {
      margin-left: 12px;
    }
  }
`;

const BatchOperationsContainer = styled.div<{ $isIssuedInvoices?: boolean }>`
  height: 76px;
  width: calc(100% - 8px);
  background: var(--color-text-title);
  border-radius: 8px 8px 0px 0px;

  display: flex;
  align-items: center;

  padding-left: 8px;
  position: sticky;
  top: 0;
  z-index: 2;

  .selectedInfoArea p {
    font-size: 16px;
    color: var(--color-light-0);
    font-family: var(--font-default-paragraph);
  }

  button {
    padding: 8px 16px;
    font-size: 16px;
    cursor: pointer;
  }

  .closeButton {
    display: flex;

    &:hover {
      background: transparent;
    }
  }

  & > * {
    margin-left: 8px;
  }

  & > menu {
    top: 60px;
    left: 410px;
  }
`;

export type TSelection = "multiSelect" | "singleSelect" | "noSelect";

export type TRow = "singleRow" | "doubleRow" | "tripleRow";

export type TCellStyleVariants =
  | "bold"
  | "link"
  | "default"
  | "miscellaneous"
  | TTagColorVariants
  | "button";

type TCellTag<T extends TTagIconVariants> = T extends "noIcon"
  ? TTagNoIcon & {
      style: TTagColorVariants;
    }
  : TTagWithIcon & {
      style: TTagColorVariants;
    };

type TCellNotTag = {
  style: "bold" | "default" | "faded" | "alert" | "link";
};

export type TSkeletonConfig = {
  variant?: "box" | "circle" | "square";
  width?: string | number;
  height?: string | number;
  style?: CSSProperties;
};

export type TText = {
  style: "bold" | "default" | "faded" | "alert" | "link";
  text: string;
  shouldBreakLine: boolean;
  marginRight?: string;
  fontSize?: string;
};

export type TCellNotTagMiscellaneous = {
  style: "miscellaneous";
  values: TText[];
  skeleton?: {
    containerCss?: CSSProperties;
    values: TSkeletonConfig[];
  };
  tooltip?: {
    text: TText[];
    customTextAlignment?: Property.TextAlign;
    size?: "large" | "default";
    hasScrollableRef?: boolean;
  };
};

type TCellCommonProps = {
  value: string;
  skeleton?: {
    containerCss?: CSSProperties;
    values: TSkeletonConfig[];
  };
  marginRight?: string;
  fontSize?: string;
  shouldBreakLine?: boolean;
  url?: string | { href: string; isExternal: boolean };
};

export type TCell<T extends Exclude<TCellStyleVariants, "button">> =
  T extends TTagColorVariants
    ? TCellCommonProps & TCellTag<TTagIconVariants>
    : T extends "miscellaneous"
    ? TCellNotTagMiscellaneous
    : TCellCommonProps & TCellNotTag;

type TNoDropdownCellButton = {
  icon: Exclude<TWithIconVariants, "ellipsisV">;
  onClick?: () => void;
};

type TDropdownCellButton = {
  icon: "ellipsisV";
  content: TContent[];
  onMenuSelect?: (selected: string) => void;
  onClick?: () => void;
  hasScrollableRef?: boolean;
};

export type TCellButton<TButtonVariant extends TWithIconVariants> =
  TButtonVariant extends "ellipsisV"
    ? TDropdownCellButton & {
        style: "button";
        isDisabled?: boolean;
        skeleton?: {
          containerCss?: CSSProperties;
          values: TSkeletonConfig[];
        };
        id?: string;
        "data-testid"?: string;
      }
    : TNoDropdownCellButton & {
        style: "button";
        isDisabled?: boolean;
        customCSS?: CSSProperties;
        id?: string;
        "data-testid"?: string;
        tooltip?: {
          text: TText[];
          customTextAlignment?: Property.TextAlign;
          size?: "large" | "default";
          hasScrollableRef?: boolean;
        };
        skeleton?: {
          containerCss?: CSSProperties;
          values: TSkeletonConfig[];
        };
      };

export type TArrayCell<T> = T[] & { forceFlex?: boolean };

export type TTableBody<TCellStyle extends TCellStyleVariants> =
  TCellStyle extends "button"
    ? TCellButton<TWithIconVariants>[]
    :
        | TCell<Exclude<TCellStyle, "button">>
        | TArrayCell<
            | TCell<Exclude<TCellStyle, "button">>
            | TCellButton<TWithIconVariants>
          >;

export type TSize = "default" | "small";

export type TTableHeadIcons = "infoCircle";

export type TTableHeadIconColors = "--color-text-labels";

export type TTableHeadIconStyles = "noIcon" | TTableHeadIcons;

type TTableHeadNoIcon = {
  style: "noIcon";
};

export type TFullData<T extends TSelection> = T extends "multiSelect"
  ? { type: "multiSelect"; fullData: any[]; initialSelectedState?: any[] }
  : {
      type: T;
      fullData?: never;
      initialSelectedState?: never;
    };

export type TTableHeadWithIcon = {
  style: "icon";
  icon: TTableHeadIcons;
  iconPosition: "left" | "right";
  iconColor: TTableHeadIconColors;
  tooltip?: {
    text: TText[];
    customTextAlignment?: Property.TextAlign;
    size?: "large" | "default";

    hasScrollableRef?: boolean;
  };
};

type TTableHeadCommonProps = {
  value: string;
  showSkeleton?: boolean;
};

export type TTableSelected =
  | { isChecked: boolean; elementId: string }
  | { isChecked?: never; elementId: "all" }
  | { isChecked?: never; elementId: "none" };

export type TTableHead<T extends TTableHeadIconStyles> = T extends "noIcon"
  ? TTableHeadNoIcon & TTableHeadCommonProps
  : TTableHeadWithIcon & TTableHeadCommonProps;

type TCommonProps = {
  "data-testid"?: string;
  customHeight?: string;
  customMarginTop?: string;
  tableHead: {
    customTopPosition?: string;
    customPaddingTop?: string;
    value: TTableHead<TTableHeadIconStyles>[];
  };
  tableBody: {
    elementId: string;
    file_xml: null | string;
    file_nfe: null | string;
    file_boleto?: string;
    rowTooltip?: {
      size?: "default" | "large";
      text: TText[];
      customTextAlignment?: Property.TextAlign;
    };
    body: (
      | TTableBody<TCellStyleVariants>
      | (NonNullable<ReactNode> & {
          skeleton?: {
            containerCss?: CSSProperties;
            values: TSkeletonConfig[];
          };
        })
    )[];
  }[];
  columns: { tableHead: { width: string[] }; tableBody: { width: string[] } };
  selectType: TFullData<TSelection>;
  disableBatchOperations?: boolean;
  onSelect?: (selected: TTableSelected[]) => void;
  onAction?: () => void;
  search?: {
    placeholder: string;
    id?: string;
    onSearch?: (searchPrase: string) => void;
    onClear?: () => void;
    $ref?: (
      inputRef: React.MutableRefObject<HTMLInputElement | null>,
      setSearchString: React.Dispatch<React.SetStateAction<string>>
    ) => void;
  };
  setSelectedRows?: number[];
  emptyState?: {
    isEmptyState: boolean;
    title: string;
    description: string;
  };
  footer?: {
    "data-testid"?: string;
    customStyle?: { width: string };
    leftSideDescription?: string;
    onSelectItemsPerPage?: (amount: number) => void;
    description?: string;
    formButtons?: {
      buttons: TButtonProps<TButtonIconVariants>[];
    };
    pagination?: {
      buttonsAmount: number;
      onClick: (activeIndex: number) => void;
      isFixedAtTheBottom?: boolean;
    };
    customPagination?: {
      fixedBottom?: {
        isFixedAtTheBottom?: boolean;
        customBottomPosition?: string;
      };
      currentPage: string;
      buttonBackward?: { onClick: () => void; disabled: boolean };
      buttonForward?: { onClick: () => void; disabled: boolean };
      pages: { value: string; onClick: () => void }[];
    };
  };
  isLoading?: boolean;
};

export type TOldSecondRow = {
  params: {
    data: TCellNotTagMiscellaneous;
    onClick?: () => void;
    onDelete?: (selected: any) => void;
  }[];
  customStyles?: {
    disable?: boolean;
    width: string;
  };
};

type TSecondRowCommonProps = {
  params: {
    data: TCellNotTagMiscellaneous;
    leftSideCustomNode?: ReactNode;
    rightSideCustomNode?: ReactNode;
    customLinkContainer?: {
      hasDoubleRow: boolean;
      node?: ReactNode;
      trCSSProperties?: React.CSSProperties;
    };
  }[];
};

export type TSecondRow = TSecondRowCommonProps;

export type TTableProps<TRowVariant extends TRow> =
  TRowVariant extends "tripleRow"
    ? TCommonProps & {
        row: "tripleRow";
        borderColor?: never;
        secondRow: TSecondRow;
        thirdRow: TSecondRow;
      }
    : TRowVariant extends "doubleRow"
    ? TCommonProps & {
        row: "doubleRow";
        borderColor?: never;
        secondRow: TSecondRow;
        thirdRow?: never;
      }
    : TRowVariant extends "singleRow"
    ? TCommonProps & {
        row: "singleRow";
        borderColor?: string[];
        secondRow?: never;
        thirdRow?: never;
      }
    : never;

const TableGrid: React.FC<TTableProps<TRow>> = ({
  "data-testid": dataTestId,
  customHeight,
  customMarginTop,
  tableHead,
  tableBody,
  borderColor,
  row,
  secondRow,
  thirdRow,
  selectType,
  disableBatchOperations,
  search,
  emptyState,
  footer,
  onSelect,
  onAction,
  isLoading,
  columns,
}) => {
  const cancelTokenSource = useRef<CancelTokenSource | null>(null);

  const tbodyRef = useRef<HTMLTableSectionElement | null>(null);
  const [clickElementListId, setClickElementListId] = useState([""]);

  const [localSelectedRowDetails, setLocalSelectedRowDetails] = useState<any[]>(
    []
  );

  const theadCheckboxSetSelectedRef = useRef<React.Dispatch<
    React.SetStateAction<"" | "active" | "inactive">
  > | null>(null);

  const [selected, setSelected] = useState<TManifestationStatusOptions>(
    "Confirmação da operação"
  );

  const [shouldDecreaseHeight, setShouldDecreaseHeight] = useState(false);

  const dispatch = useTypedDispatch();

  const [shouldShowManifestationModal, setShouldShowManifestationModal] =
    useState(false);

  const selectedRowsRef = useRef<
    {
      isChecked: boolean;
      clickedElementId: string;
    }[]
  >([]);

  const selectedOrganization = useTypedSelector(
    (state) => state.organizations.selectedOrganization
  );

  const reduxQuerystring = useTypedSelector((state) => state.querystring);
  const forceSelectElementsRef = useRef<
    | {
        elementId: string;
        isClicked: "" | "active" | "inactive";
        setSelectElement: React.Dispatch<
          React.SetStateAction<"" | "active" | "inactive">
        >;
      }[]
  >([]);

  const skipRescueRef = useRef(false);

  const selectedOrganizationRef = useRef<TOrganization | null>(null);
  const currPageRef = useRef<number | undefined>();
  const fullDataRef = useRef<typeof selectType.fullData>();

  const tableContainerId = useId();
  const tableId = useId();
  const tfootDataId = useId();

  const [justificationText, setJustificationText] = useState<
    string | undefined
  >();
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  const TEXT_AREA_MAX_lENGTH = 256;

  const handleTextAreaOnInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
    setTimeout(() => {
      if (
        textAreaRef.current &&
        textAreaRef.current.value.length <= TEXT_AREA_MAX_lENGTH
      ) {
        setJustificationText((e.target as HTMLTextAreaElement).value);
      }
    }, 1000);
  };

  const multiSelectHandler = (
    selected: {
      isChecked: boolean;
      clickedElementId: string;
      setClickState?: React.Dispatch<
        React.SetStateAction<"" | "active" | "inactive">
      >;
    },
    _prev: string[]
  ) => {
    let newSelectedIds = _prev.filter(
      (id: string) => id !== undefined && id !== "all"
    );
    let newSelectedDetails = [];

    // Remove "all" from selection if needed
    if (!selected.isChecked && newSelectedIds.includes("all")) {
      newSelectedIds = newSelectedIds.filter((id: string) => id !== "all");
    }

    // Find the table body element corresponding to the clicked element ID
    const tableBodyElement = fullDataRef.current?.find(
      (element) => element.uuid === selected.clickedElementId
    );

    if (!tableBodyElement) {
      console.warn(
        `Element with ID ${selected.clickedElementId} not found in fullDataRef.`
      );
      return _prev;
    }

    if (!selected.isChecked) {
      // Deselect
      newSelectedIds = newSelectedIds.filter(
        (id: string) => id !== selected.clickedElementId
      );
    } else {
      // Select
      if (!newSelectedIds.includes(selected.clickedElementId)) {
        newSelectedIds.push(selected.clickedElementId);
      }
    }

    // Build new details array (filter out nulls for type safety)
    newSelectedDetails = newSelectedIds
      .map((id: string) => {
        const row = fullDataRef.current?.find((el) => el.uuid === id);
        if (!row) return undefined;
        return {
          elementId: row.uuid,
          invoiceNfe: row.file_nfe,
          invoiceXml: row.file_xml,
          paymentSlipFile: row.file_boleto,
        };
      })
      .filter(
        (
          detail:
            | {
                elementId: string;
                invoiceNfe: any;
                invoiceXml: any;
                paymentSlipFile: any;
              }
            | undefined
        ): detail is {
          elementId: string;
          invoiceNfe: any;
          invoiceXml: any;
          paymentSlipFile: any;
        } => detail !== undefined
      );

    // Handle the "none" case if no rows are selected
    if (newSelectedIds.length === 0) {
      setLocalSelectedRowDetails([]);
      if (onSelect) onSelect([{ elementId: "none" }]);
      return [];
    }

    // Update Redux state
    setLocalSelectedRowDetails(newSelectedDetails);

    // Trigger the `onSelect` callback
    if (onSelect) {
      onSelect(
        newSelectedIds.map((id: string) => ({
          elementId: id,
          isChecked: true,
        }))
      );
    }

    return newSelectedIds;
  };

  const singleSelectHandler = (
    selected: {
      isChecked: boolean;
      clickedElementId: string;
      setClickState?: React.Dispatch<
        React.SetStateAction<"" | "active" | "inactive">
      >;
    },
    _prev: string[]
  ) => {
    const tableBodyElement = tableBody.find(
      (element) => `${element.elementId}` === selected.clickedElementId
    );

    if (!selected.isChecked) {
      setLocalSelectedRowDetails([]);
      if (onSelect) {
        onSelect([
          {
            isChecked: false,
            elementId: selected.clickedElementId,
          },
        ]);
      }
      return [];
    } else {
      if (tableBodyElement) {
        setLocalSelectedRowDetails([
          {
            elementId: tableBodyElement.elementId,
            invoiceNfe: tableBodyElement.file_nfe,
            invoiceXml: tableBodyElement.file_xml,
            paymentSlipFile: tableBodyElement.file_boleto,
          },
        ]);
      } else {
        setLocalSelectedRowDetails([]);
      }
      if (onSelect) {
        onSelect([
          {
            isChecked: true,
            elementId: selected.clickedElementId,
          },
        ]);
      }
      return [selected.clickedElementId];
    }
  };

  const handleOnSelectRow = (selected: {
    isChecked: boolean;
    clickedElementId: string;
    setClickState?: React.Dispatch<
      React.SetStateAction<"" | "active" | "inactive">
    >;
  }) => {
    // Use Redux for all selection state

    if (fullDataRef.current) {
      console.log(
        "selected: ",
        selected,
        ", fulldata: ",
        fullDataRef.current,
        ", selected row details: ",
        localSelectedRowDetails
      );
      const prevIds = localSelectedRowDetails.map((row) => row.elementId);
      multiSelectHandler(selected, prevIds);
    } else {
      singleSelectHandler(selected, []);
    }

    // Optionally handle theadCheckboxSetSelectedRef for UI feedback
    if (
      theadCheckboxSetSelectedRef.current &&
      fullDataRef.current &&
      selected.setClickState &&
      selected.isChecked === false
    ) {
      theadCheckboxSetSelectedRef.current("inactive");
      selected.setClickState("inactive");
    }
  };

  const handleFetchInvoices = () => {
    // All filter, pagination, and organization state should be in Redux
    // Just dispatch the fetchInvoices thunk, which should use selectors for all state
    // fetchInvoices expects a cancelToken argument
    if (!cancelTokenSource.current) {
      cancelTokenSource.current = axios.CancelToken.source();
    }
    dispatch(fetchInvoices({ cancelToken: cancelTokenSource.current.token }));
  };

  const decideDownloadFileBatchOperation = (
    option: "xml" | "darf" | "paymentSlip"
  ) => {
    if (cancelTokenSource.current) {
      dispatch(
        setToastState({
          variant: "loading",
          message: {
            title: "Compactando...",
            description:
              "Após compactar os arquivos, o download começará automaticamente",
          },
          shouldShow: true,
        })
      );
      setTimeout(() => {
        const isIssuedInvoices =
          window.location.pathname.includes("/invoices/issued/");

        (option === "paymentSlip"
          ? dispatch(
              downloadPaymentSlipsCompressedFiles({
                itemsUuid: localSelectedRowDetails
                  .filter((element) => element.paymentSlipFile !== null)
                  .map((element) => element.elementId),
                cancelToken: (cancelTokenSource.current as CancelTokenSource)
                  .token,
              })
            )
          : isIssuedInvoices
          ? dispatch(
              // Notas emitidas: endpoint próprio; "darf" (DANFE) vira "pdf".
              downloadIssuedInvoicesCompressedFiles({
                option: option === "xml" ? "xml" : "pdf",
                itemsUuid: localSelectedRowDetails
                  .filter((element) =>
                    option === "xml"
                      ? element.invoiceXml !== null
                      : element.invoiceNfe !== null
                  )
                  .map((element) => element.elementId),
                cancelToken: (cancelTokenSource.current as CancelTokenSource)
                  .token,
              })
            )
          : dispatch(
              downloadInvoicesCompressedFiles({
                option,
                itemsUuid: localSelectedRowDetails
                  .filter((element) =>
                    option === "xml"
                      ? element.invoiceXml !== null
                      : element.invoiceNfe !== null
                  )
                  .map((element) => element.elementId),
                cancelToken: (cancelTokenSource.current as CancelTokenSource)
                  .token,
              })
            )
        )
          .unwrap()
          .then((response) => {
            dispatch(
              setToastState({
                variant: "loading",
                shouldShow: false,
                message: { title: "", description: "" },
              })
            );

            window.open(response?.url, "_blank");
          })
          .catch((_e) => {
            console.log("error: ", _e);
            dispatch(
              setToastState({
                variant: "loading",
                shouldShow: false,
                message: { title: "", description: "" },
              })
            );

            setTimeout(() => {
              dispatch(
                setToastState({
                  variant: "error",
                  shouldShow: true,
                  message: {
                    title: "Erro",
                    description: "Não foi possível concluir a ação",
                  },
                })
              );
            }, 100);
          });
      }, 1500);
    }
  };

  const handleDownloadFileBatchOperation = (
    variant: "xml" | "nfe" | "paymentSlip"
  ) => {
    cancelTokenSource.current = axios.CancelToken.source();

    switch (variant) {
      case "xml":
        decideDownloadFileBatchOperation("xml");
        break;
      case "nfe":
        decideDownloadFileBatchOperation("darf");
        break;
      case "paymentSlip":
        decideDownloadFileBatchOperation("paymentSlip");
        break;
    }
  };

  const handleSettingManifestationBatchOperation = () => {
    cancelTokenSource.current = axios.CancelToken.source();

    selectedOrganizationRef.current = selectedOrganization;

    currPageRef.current = reduxQuerystring.page;

    function getManifestationCode(
      status: TManifestationStatusOptions
    ): TInvoiceManifestastionStatus {
      switch (status) {
        case "Ciência da emissão":
          return 10;
        case "Confirmação da operação":
          return 20;
        case "Desconhecimento da operação":
          return 30;
        case "Operação não realizada":
          return 40;
        default:
          return 20;
      }
    }
    const manifestation: TInvoiceManifestastionStatus =
      getManifestationCode(selected);

    Promise.all(
      localSelectedRowDetails.map(
        (element: any) =>
          new Promise((resolve, reject) => {
            // If you need full data, you can still use fullDataRef.current

            const fullElement = fullDataRef.current?.find(
              (data) => element.elementId === data.uuid
            );

            if (fullElement && fullElement.manifestation === null) {
              apiService
                .updateInvoiceManifestation(
                  element.elementId,
                  manifestation,
                  (cancelTokenSource.current as CancelTokenSource).token,
                  justificationText
                )
                .then((response) => resolve(response))
                .catch((e) => reject(e));
            } else {
              reject("");
            }
          })
      )
    )
      .then(() => {
        console.log("all settled");
        handleFetchInvoices();
      })
      .catch((e: any) => {
        if (e !== "") {
          const errorMessage = backendErrorMessageHandler(e);

          dispatch(
            setToastState({
              variant: "error",
              shouldShow: true,
              message: {
                title: "Erro ao fazer manifestação",
                description:
                  errorMessage ??
                  "Ocorreu um erro ao fazer manifestação, tente novamente",
              },
            })
          );
        }
      })
      .finally(() => {
        setSelected("Confirmação da operação" as TManifestationStatusOptions);
        if (onAction) onAction();
      });

    setShouldShowManifestationModal(false);
  };

  const handlePaginationBackwardState = () => {
    if (footer?.customPagination?.buttonBackward) {
      footer.customPagination.buttonBackward.onClick();
    }
  };

  const handlePaginationForwardState = () => {
    if (footer?.customPagination?.buttonForward) {
      footer.customPagination.buttonForward.onClick();
    }
  };

  const handleRenderFooterCustomPaginationButtons = () => {
    if (footer?.customPagination) {
      return footer.customPagination.pages.map((page, index) => (
        <Button
          key={index}
          icon="onlyIcon"
          iconType="string"
          variant="secondary"
          text={`${page.value}`}
          shouldFontWeightNotBeBold={
            page.value !== footer.customPagination?.currentPage
          }
          onClick={() => {
            page.onClick();
          }}
          className={
            page.value === footer.customPagination?.currentPage ? "active" : ""
          }
        />
      ));
    }
    return <></>;
  };

  // Keep fullDataRef.current in sync with selectType.fullData for multiSelect
  useEffect(() => {
    if (selectType.type === "multiSelect" && selectType.fullData) {
      fullDataRef.current = selectType.fullData;
    }
  }, [selectType.type, selectType.fullData]);

  // --- Redux-driven selection state initialization ---
  useEffect(() => {
    if (
      selectType.type === "multiSelect" &&
      selectType.initialSelectedState &&
      selectType.initialSelectedState.length > 0
    ) {
      // Deep clone to prevent mutation of the prop
      const initialSelectedState = JSON.parse(
        JSON.stringify(selectType.initialSelectedState)
      );
      const details = initialSelectedState.map((row: any) => ({
        elementId: row.uuid,
        invoiceNfe: row.file_nfe,
        invoiceXml: row.file_xml,
        paymentSlipFile: row.file_boleto,
      }));
      setLocalSelectedRowDetails(details);
    }
    // No else: singleSelect handled elsewhere
  }, [
    selectType.fullData,
    selectType.initialSelectedState,
    selectType.type,
    dispatch,
  ]);

  // --- Redux-driven UI sync for selection state ---
  useEffect(() => {
    // Sync UI selection state with Redux selection
    if (selectType.type !== "multiSelect") return;
    // If nothing selected but we have a previous selection, try to rescue
    // Only run rescue if there is at least one selectable row and the last selection was not 'none'
    const lastSelectionWasNone =
      localSelectedRowDetails.length === 0 &&
      // Check if Redux selection was just cleared by a 'none' action
      ((Array.isArray(selectedRowsRef.current) &&
        selectedRowsRef.current.length === 0) ||
        (Array.isArray(clickElementListId) &&
          clickElementListId[0] === "none"));

    if (
      localSelectedRowDetails.length === 0 &&
      forceSelectElementsRef.current.length > 0 &&
      forceSelectElementsRef.current[0].elementId &&
      !lastSelectionWasNone
    ) {
      const rescuedValue = forceSelectElementsRef.current.find(
        (element) =>
          element.elementId === forceSelectElementsRef.current[0].elementId
      );
      if (rescuedValue) {
        setLocalSelectedRowDetails([
          {
            elementId: rescuedValue.elementId,
            invoiceNfe: null,
            invoiceXml: null,
            paymentSlipFile: undefined,
          },
        ]);

        setTimeout(() => {
          rescuedValue.setSelectElement("active");
        }, 20);
      }
    }
    // If "all" is selected, activate all UI elements
    if (localSelectedRowDetails.some((row) => row.elementId === "all")) {
      forceSelectElementsRef.current.forEach((element) => {
        if (element.isClicked !== "active") element.setSelectElement("active");
      });
    } else if (localSelectedRowDetails.length > 0) {
      setTimeout(() => {
        forceSelectElementsRef.current.forEach((element) => {
          if (
            localSelectedRowDetails.some(
              (row) => row.elementId === element.elementId
            )
          ) {
            element.setSelectElement("active");
          }
        });
      });
    }
  }, [tableBody, localSelectedRowDetails, selectType.type, dispatch]);

  useEffect(() => {
    // Deselect all rows and deactivate batch container when launch_status querystring changes
    if (!disableBatchOperations) {
      setClickElementListId(["none"]);
      setLocalSelectedRowDetails([]);
    }
  }, [reduxQuerystring.launch_status, dispatch]);

  // --- Redux-driven UI sync for clearing selection ---
  useEffect(() => {
    if (selectType.type !== "multiSelect") return;
    if (localSelectedRowDetails.some((row) => row.elementId === "none")) {
      forceSelectElementsRef.current.forEach((element) => {
        if (element.isClicked !== "") element.setSelectElement("");
      });
    }
  }, [localSelectedRowDetails, selectType.type]);

  useEffect(() => {
    setShouldDecreaseHeight(false);
    setTimeout(() => {
      const bodyElement = document.getElementsByTagName("body")[0];
      if (bodyElement) {
        if (bodyElement.scrollHeight > bodyElement.clientHeight) {
          setShouldDecreaseHeight(true);
        }
      }
    }, 500);

    return () => {
      if (cancelTokenSource.current) cancelTokenSource.current.cancel();
      // Clear selection state in Redux on unmount
      setLocalSelectedRowDetails([]);
    };
  }, []);

  return (
    <Container id={tableContainerId} $customMarginTop={customMarginTop}>
      {search && (
        <Search
          variant="default"
          placeholder={search.placeholder}
          id={search.id}
          onSearch={search.onSearch}
          onClear={search.onClear}
          $ref={search.$ref}
        />
      )}

      {selectType.type === "multiSelect" &&
        !disableBatchOperations &&
        localSelectedRowDetails.length > 0 && (
          <BatchOperationsContainer
            key={
              localSelectedRowDetails.length +
              "-batchOperationContainer-" +
              clickElementListId.length
            }
            $isIssuedInvoices={window.location.pathname.includes(
              "/invoices/issued/"
            )}
          >
            <div
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
                width: window.location.pathname.includes("issued")
                  ? "1064px"
                  : "672px",
              }}
            >
              <div className="selectedInfoArea">
                <p data-testid="selected-info-count">
                  {localSelectedRowDetails.length} selecionado
                  {localSelectedRowDetails.length === 1 ? "" : "s"}
                </p>
              </div>
              {window.location.pathname.includes("invoices") && (
                <>
                  <Button
                    variant="white"
                    icon="noIcon"
                    text="Baixar XML"
                    disabled={
                      !localSelectedRowDetails.some(
                        (element) => element.invoiceXml !== null
                      )
                    }
                    onClick={() => {
                      handleDownloadFileBatchOperation("xml");
                    }}
                  />
                  <Button
                    variant="white"
                    icon="noIcon"
                    text="Baixar DANFE"
                    disabled={
                      !localSelectedRowDetails.some(
                        (element) => element.invoiceNfe !== null
                      )
                    }
                    onClick={() => {
                      handleDownloadFileBatchOperation("nfe");
                    }}
                  />
                  {window.location.pathname.includes("issued") ? (
                    <div
                      style={{
                        color: "var(--color-light-0)",
                        fontSize: 16,
                        display: "flex",
                        gap: 8,
                        alignItems: "center",
                      }}
                    >
                      <UilInfoCircle width={16} height={16} />
                      <p>
                        Deseja exportar os XMLs em massa?{" "}
                        <a
                          href={"/invoices/issued/export/" + window.location.search}
                          style={{
                            color: "var(--color-text-link)",
                            cursor: "pointer",
                          }}
                        >
                          Clique aqui
                        </a>
                      </p>
                    </div>
                  ) : (
                    <Button
                      variant="white"
                      icon="noIcon"
                      text="Alterar status manifestação"
                      onClick={() => {
                        setShouldShowManifestationModal(true);
                      }}
                      disabled={
                        !localSelectedRowDetails.some(
                          (element) =>
                            fullDataRef.current?.find(
                              (data) => element.elementId === data.uuid
                            )?.manifestation === null
                        )
                      }
                    />
                  )}
                </>
              )}
              {window.location.pathname.includes("payment-slip") && (
                <>
                  <Button
                    variant="white"
                    icon="noIcon"
                    text="Baixar Boleto"
                    disabled={
                      !localSelectedRowDetails.some(
                        (element) => element.paymentSlipFile !== null
                      )
                    }
                    onClick={() => {
                      handleDownloadFileBatchOperation("paymentSlip");
                    }}
                  />
                </>
              )}
              {shouldShowManifestationModal && (
                <ManifestationModal
                  $shouldshow={shouldShowManifestationModal ? "show" : "hide"}
                  style={{ left: "410px", top: "60px" }}
                >
                  <h2>Alterar status manifestação</h2>

                  <Dropdown
                    button={{
                      variant: "unlabeled",
                      placeholder: "",
                      labels: ["Ciência da emissão"],
                    }}
                    menu={{
                      variant: "unlabeled",
                      options: [
                        "Ciência da emissão",
                        "Confirmação da operação",
                        "Desconhecimento da operação",
                        "Operação não realizada",
                      ],
                      search: false,
                      forceSelectOptionsByIndex: [0],
                      onSelect(index) {
                        setSelected(index as TManifestationStatusOptions);
                      },
                    }}
                  />
                  {[
                    "Desconhecimento da operação",
                    "Operação não realizada",
                  ].includes(selected) && (
                    <TextAreaContainer
                      $placeholder="Justificativa (mínimo de 15 caracteres) "
                      $justificationText={justificationText}
                      role="button"
                      onClick={() => {
                        if (textAreaRef.current !== null)
                          textAreaRef.current.focus();
                      }}
                    >
                      <TextArea
                        ref={textAreaRef}
                        minLength={15}
                        maxLength={TEXT_AREA_MAX_lENGTH}
                        onInput={handleTextAreaOnInput}
                      />
                      <span className="mandatory">&nbsp;*</span>
                    </TextAreaContainer>
                  )}
                  <FilterArea>
                    <div className="buttonsArea">
                      <Button
                        variant="secondary"
                        icon="noIcon"
                        text="Cancelar"
                        onClick={() => {
                          setShouldShowManifestationModal(false);
                          setSelected("Confirmação da operação");
                        }}
                      />
                      <Button
                        disabled={
                          [
                            "Desconhecimento da operação",
                            "Operação não realizada",
                          ].includes(selected) &&
                          (justificationText === undefined ||
                            justificationText.length < 15)
                        }
                        variant="primary"
                        icon="checkmark"
                        iconPosition="left"
                        text="Salvar alteração"
                        onClick={handleSettingManifestationBatchOperation}
                      />
                    </div>
                  </FilterArea>
                </ManifestationModal>
              )}
            </div>
            <div
              style={{
                display: "flex",
                width: "calc(100% - 688px)",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => {
                  setClickElementListId(["none"]);
                  setLocalSelectedRowDetails([]);
                  if (theadCheckboxSetSelectedRef.current) {
                    theadCheckboxSetSelectedRef.current("inactive");
                  }
                  if (onSelect) onSelect([{ elementId: "none" }]);
                }}
                className="closeButton"
              >
                <UilTimes style={{ color: "var(--color-light-0)" }} />
              </button>
            </div>
          </BatchOperationsContainer>
        )}

      <TableContainer
        $isLoading={isLoading}
        $customHeight={customHeight}
        $isBatchOperationsContainerVisible={
          selectType.type === "multiSelect" &&
          !disableBatchOperations &&
          localSelectedRowDetails.length > 0
        }
        id={tableId}
        data-testid={dataTestId}
        key={localSelectedRowDetails.length + "-thead"}
      >
        <THead
          onSelect={(clickStatus, setSelected) => {
            theadCheckboxSetSelectedRef.current = setSelected;

            switch (clickStatus) {
              case "all": {
                forceSelectElementsRef.current.forEach((element) => {
                  element.setSelectElement("active");
                });

                const ids: string[] = [];
                selectedRowsRef.current = [];
                let details: any[] = [];
                if (fullDataRef.current) {
                  details = fullDataRef.current
                    .map((element) => {
                      if (!ids.includes(element.uuid)) {
                        selectedRowsRef.current.push({
                          clickedElementId: element.uuid,
                          isChecked: true,
                        });
                        ids.push(element.uuid);
                        return {
                          elementId: element.uuid,
                          invoiceNfe: element.file_nfe,
                          invoiceXml: element.file_xml,
                          paymentSlipFile: element.file_boleto,
                        };
                      }
                      return undefined;
                    })
                    .filter((element) => element !== undefined);
                }
                setClickElementListId(["all", ...ids]);

                // Update Redux selection state
                setLocalSelectedRowDetails(details);

                if (onSelect) onSelect([{ elementId: "all" }]);
                break;
              }
              case "none": {
                setClickElementListId(["none"]);
                // Removed clickElementListRef usage
                selectedRowsRef.current = [];

                // Update Redux selection state
                setLocalSelectedRowDetails([]);

                skipRescueRef.current = true;

                if (onSelect) onSelect([{ elementId: "none" }]);
                break;
              }
            }
          }}
          selectType={
            emptyState?.isEmptyState ? { type: "noSelect" } : selectType
          }
          tableHead={tableHead}
          gridTemplateColumns={(() => {
            const result = columns.tableHead.width
              .map((width) => width)
              .join(" ");
            return result;
          })()}
          checked={(() => {
            // Only for multiSelect
            if (selectType.type !== "multiSelect" || !selectType.fullData)
              return false;
            const total = selectType.fullData.length;
            const selected = localSelectedRowDetails.length;
            if (selected === 0) return false;
            if (selected === total) return true;
            return false;
          })()}
          indeterminate={(() => {
            if (selectType.type !== "multiSelect" || !selectType.fullData)
              return false;
            const total = selectType.fullData.length;
            const selected = localSelectedRowDetails.length;
            return selected > 0 && selected < total;
          })()}
        />
        {emptyState?.isEmptyState ? (
          <EmptyStateTBody className="emptyState">
            <tr>
              <td colSpan={tableHead.value.length + 2}>
                <div>
                  <SVG src={emptyStateSVG} />
                </div>
              </td>
            </tr>
            <tr>
              <td colSpan={tableHead.value.length + 2}>
                <div>
                  <h2>{emptyState.title}</h2>
                </div>
              </td>
            </tr>
            <tr>
              <td colSpan={tableHead.value.length + 2}>
                <div>
                  <p>{emptyState.description}</p>
                </div>
              </td>
            </tr>
            <tr>
              <td colSpan={tableHead.value.length + 2}>
                <div></div>
              </td>
            </tr>
          </EmptyStateTBody>
        ) : (
          <TBody
            key={localSelectedRowDetails.length + "-tbody"}
            tbodyRef={tbodyRef}
            isLoading={isLoading}
            gridTemplateColumns={columns.tableBody.width
              .map((width) => width)
              .join(" ")}
            hasFooter={(footer && "hasFooter") || "noFooter"}
            forceSelectElementRef={forceSelectElementsRef}
            onRowSelect={handleOnSelectRow}
            selectType={selectType}
            tableBody={tableBody}
            borderColor={borderColor}
            row={row}
            secondRow={secondRow}
            thirdRow={thirdRow}
          />
        )}
        {!emptyState?.isEmptyState && footer && (
          <>
            <TableFooter
              $isFixedAtTheBottom={
                footer.customPagination?.fixedBottom?.isFixedAtTheBottom
              }
              $customBottomPosition={
                footer.customPagination?.fixedBottom?.customBottomPosition
              }
              $customStyles={footer?.customStyle}
            >
              <tr>
                <td colSpan={tableHead.value.length + 3}>
                  <div id={tfootDataId} className="footerDataContainer">
                    <div className="leftSideFooterDataContainer">
                      {isLoading ? (
                        // Skeleton array for loading state
                        <div
                          style={{
                            display: "flex",
                            width: "660px",
                            gap: "4px",
                          }}
                        >
                          {[
                            { width: "40px", height: "22px" },
                            { width: "45px", height: "22px" },
                            {
                              width: "120px",
                              height: "22px",
                            },
                            {
                              width: "220px",
                              height: "22px",
                            },
                          ].map((skeletonProps, idx) => (
                            <Skeleton key={idx} {...skeletonProps} />
                          ))}
                        </div>
                      ) : (
                        footer.leftSideDescription && (
                          <>
                            <p>Exibir </p>
                            <Dropdown
                              button={{
                                variant: "unlabeled",
                                labels: (() => {
                                  const querystring = getQuerystringParams();

                                  const options = ["100", "150", "200"];

                                  const itemsPerPage =
                                    querystring.items_per_page.toString();

                                  if (
                                    querystring.items_per_page &&
                                    options.includes(itemsPerPage)
                                  ) {
                                    return [
                                      options[options.indexOf(itemsPerPage)],
                                    ];
                                  }
                                  return [options[0]];
                                })(),
                                placeholder: "",
                              }}
                              menu={{
                                shouldHideActiveIcon: true,
                                variant: "unlabeled",
                                options: ["100", "150", "200"],
                                forceSelectOptionsByIndex: (() => {
                                  const querystring = getQuerystringParams();

                                  const options = ["100", "150", "200"];

                                  const itemsPerPage =
                                    querystring.items_per_page.toString();

                                  if (
                                    querystring.items_per_page &&
                                    options.includes(itemsPerPage)
                                  ) {
                                    return [options.indexOf(itemsPerPage)];
                                  }
                                  return [0];
                                })(),
                                onSelect(selected) {
                                  if (footer.onSelectItemsPerPage)
                                    footer.onSelectItemsPerPage(
                                      parseInt(selected)
                                    );
                                },
                                search: false,
                              }}
                            />
                            <p> resultados por página</p>
                            {footer.customPagination &&
                              footer.customPagination.pages.length < 12 && (
                                <p>{footer.leftSideDescription}</p>
                              )}
                          </>
                        )
                      )}
                    </div>
                    <div className="rightSideFooterDataContainer">
                      {isLoading ? (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          {(
                            [
                              {
                                width: "32px",
                                height: "32px",
                                variant: "square",
                              },
                              {
                                width: "32px",
                                height: "32px",
                                variant: "square",
                              },
                              {
                                width: "32px",
                                height: "32px",
                                variant: "square",
                              },
                            ] as TSkeletonProps[]
                          ).map((skeletonProps, idx) => (
                            <Skeleton key={idx} {...skeletonProps} />
                          ))}
                        </div>
                      ) : (
                        <>
                          {footer.description &&
                            footer.customPagination &&
                            footer.customPagination.pages.length < 6 && (
                              <p>{footer.description}</p>
                            )}
                          <Button
                            icon="onlyIcon"
                            iconType="angleLeft"
                            variant="secondary"
                            onClick={handlePaginationBackwardState}
                            disabled={
                              footer.customPagination?.buttonBackward?.disabled
                            }
                          />
                          {handleRenderFooterCustomPaginationButtons()}
                          <Button
                            icon="onlyIcon"
                            iconType="angleRight"
                            variant="secondary"
                            onClick={handlePaginationForwardState}
                            disabled={
                              footer.customPagination?.buttonForward?.disabled
                            }
                          />
                        </>
                      )}
                    </div>
                  </div>
                </td>
              </tr>
              {footer.formButtons && (
                <tr>
                  <td
                    colSpan={tableHead.value.length + 3}
                    className="footerFormButtonContainer"
                  >
                    {footer.formButtons.buttons.map((buttonProps, index) => (
                      <Button key={index} {...buttonProps} />
                    ))}
                  </td>
                </tr>
              )}
            </TableFooter>
            {footer && footer.formButtons ? (
              <div style={{ height: "168px" }} />
            ) : (
              !shouldDecreaseHeight && <div style={{ height: "80px" }} />
            )}
          </>
        )}
      </TableContainer>
    </Container>
  );
};

export default TableGrid;
